import { useEffect, useRef } from "react";
// import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { evaluateSelector } from "./evaluateSlice";
import {
  setWebSocketConnected,
  addWebSocketEvent,
  setWebSocketComplete,
  setWebSocketError,
  setWebSocketEvaluationId,
} from "./evaluateSlice";
import { websocketManager } from "@/utils/websocket";

/**
 * Global WebSocket Listener Component
 * This component manages WebSocket connections and persists across page navigation
 * It should be placed at the root level (e.g., in App.tsx or main layout)
 */
const WebSocketListener = () => {
  const dispatch = useAppDispatch();
  const { websocket } = useAppSelector(evaluateSelector);
  const hasInitialized = useRef(false);

  useEffect(() => {
    // Connect to WebSocket when taskId is available and not already connected
    if (
      websocket.taskId &&
      !websocket.isConnected &&
      !hasInitialized.current &&
      websocket.isProcessing
    ) {
      hasInitialized.current = true;

      websocketManager.connect(websocket.taskId, {
        onOpen: () => {
          dispatch(setWebSocketConnected(true));
        },
        onMessage: (event) => {
          dispatch(addWebSocketEvent(event));

          // Check if evaluation_id is received (completion indicator)
          // Handle both 'evaluation_id' (correct) and 'evaluaion_id' (backend typo)
          const evaluationId = event.data?.evaluation_id || event.data?.evaluaion_id;

          if (event.data && evaluationId) {
            // Store evaluation_id in Redux
            dispatch(setWebSocketEvaluationId(evaluationId));

            // Mark as complete
            dispatch(setWebSocketComplete(true));

            // Don't disconnect immediately, let backend close naturally
            // The connection will close with code 1000 from backend
          }
        },
        onError: (error) => {
          console.error("❌ WebSocket error:", error);
          dispatch(setWebSocketError("WebSocket connection error"));
        },
        onClose: (event) => {
          dispatch(setWebSocketConnected(false));

          // If closed normally (code 1000), don't reconnect
          // This is expected when backend closes connection after sending evaluation_id
          if (event.code === 1000) {
            // Mark as complete if not already marked
            if (websocket.isProcessing) {
              dispatch(setWebSocketComplete(true));
            }
            // Don't attempt reconnection for normal closure
            return;
          }

          // For other close codes, mark as complete if processing
          if (websocket.isProcessing) {
            dispatch(setWebSocketComplete(true));
          }
        },
      });
    }

    // Cleanup on unmount or when taskId changes
    return () => {
      if (!websocket.taskId || websocket.isComplete) {
        hasInitialized.current = false;
      }
    };
  }, [
    websocket.taskId,
    websocket.isConnected,
    websocket.isProcessing,
    dispatch,
  ]);

  // Reset initialization flag when taskId changes or processing completes
  useEffect(() => {
    if (!websocket.taskId || websocket.isComplete) {
      hasInitialized.current = false;
    }
  }, [websocket.taskId, websocket.isComplete]);

  // This component doesn't render anything
  return null;
};

export default WebSocketListener;
